import { z } from "zod";

/* ────────────────────────────────────────────────────────────────── */
/* Canonical badge dictionary                                         */
/* ────────────────────────────────────────────────────────────────── */

const BADGE_DEFS = {
  /* ——— static / business-logic badges ——— */
  booked_by_other: "Booked by Other",

  /* ——— appointment flow events ——— */
  appointment_booked: "Booked",
  appointment_rescheduled: "Rescheduled",
  appointment_cancelled: "Appt Canceled",
  appointment_confirmed: "Appt Confirmed",
  confirmed_upcoming_appointment: "RSVP: Confirmed",
  appointment_updated: "Appt Updated",
  emergency_services_notified: "Emergency Service",
  billing: "Billing",
  inquiry: "Inquiry",
  retreatment: "Retreatment",
  sales: "Sales",
  membership_sold: "Membership Sold",

  /* ——— flagged interaction events ——— */
  billing_inquiry: "Billing Inquiry",
  membership_sold_but_not_paid: "Membership Sold (Unpaid)",
  scheduling_objection: "Timing",
  price_objection: "Cost",
  ask_for_human: "Asked for Human",
  escalation_rule: "Escalated",
  text_disabled: "Text Disabled",
  service_area_issue: "Out of Area",
  project_follow_up: "Follow Up Project",
  no_appointments_found: "No Appointments",
  non_owner_occupant: "Non-Owner",
  commercial: "Commercial",
  account_update: "Account Update",
  manual_escalation: "Manual Flag",
  campaign_dismissal: "Campaign Dismissal",
  attic_job: "Attic",
  boiler_job: "Boiler",
  // Barron island-queue booked-call badges (San Juan Islands). The smaller islands flag with the
  // badge-less `island_queue` event (no def here), so they fold into the folder without a pill.
  island_orcas: "Orcas",
  island_friday_harbor: "Friday Harbor",
  island_lopez: "Lopez",
  canceled: "Canceled",
  paid_lead_inactivity: "Inactivity",
  paid_lead_no_response: "No Response",
  paid_lead: "Paid Lead",
  auto_dial: "Auto Outbound Call",
  high_value: "High Value",
  no_interaction: "No Interaction",
  landline: "Landline",
  carrier_filtered: "Carrier Filtered",
  duplicate_booking: "Duplicate Job",
  outstanding_balance: "Outstanding Balance",
  hot_lead: "Hot Lead",
  membership_inquiry: "Membership Inquiry",
  move_up_requested: "Move Up Requested",
  // other: "Other",
  // Appointment Reminders
  dangling_appointment_reschedule: "RSVP: Rescheduled",
  dangling_appointment_cancel: "RSVP: Cancelled",

  // Certus/PestPac escalation types
  customer_care: "Customer Care",
  retention: "Retention",
  inside_sales: "Inside Sales",
  outside_sales: "Outside Sales",

  // Bay Club escalation types
  golf_inquiry: "Golf Inquiry",
  membership_reactivation: "Membership Reactivation",
  general_inquiry: "General Inquiry",
  primary_cancellation: "Primary Cancellation",
  add_on_removal: "Add-On Removal",
  corporate_cancellation: "Corporate Cancellation",
  account_request: "Account Request",

  // Tragar escalation types
  oil_delivery: "Oil Delivery",

  // Transfer-related flags
  transfer_not_picked_up: "Transfer Not Picked Up",
  hung_up_before_transfer: "Hung Up Before Transfer",

  // GBP 5-star review testimonial request
  customer_testimonial: "Customer Testimonial",

  /* ——— unbooked reason ——— */
  QuoteOnly: "Quote Only",
  ObjectPrice: "Object Price",
  TooLate: "Object Timing",
  CustomerTimingConflict: "Customer Timing Conflict",
  EmergencyServiceUnavailable: "Emergency Service Unavailable",
  Callback: "Callback",
  ToFollowUp: "To Follow Up",
  Finished: "Finished",
  WentWithOther: "Went with Other",
  SpeakToThirdParty: "Speak to Third Party",

  ObjectRobot: "Disconnect",
  Disconnect: "Disconnect",
  FailConfirm: "Disconnect",
  TooManyQuestions: "Disconnect",
  Frustration: "Disconnect",

  /* ——— analytics interaction type ——— */
  Excused: "Not Lead",
  NotLead: "Not Lead",
  NoResponse: "No Response",

  /* ——— Transferred ——— */
  Transferred: "Transferred Lead",

  /* ——— ARS interaction-outcome dispositions (neutral, non-escalated) ——— */
  ars_handled: "Handled",
  ars_took_note: "Note Taken",
} as const;

// Mister Sparky's "Transferred Lead" pill. v1 raised it for `transferred lead`
// and `escalated with note`; these are the engine outcomes those land on.
// `escalated with note` also lands on Unbooked / Handled, which are deliberately
// excluded, same call as the appointment-confirmations ESCALATED bucket.
const SPARKY_TRANSFERRED_OUTCOMES = new Set<string>([
  "Transferred",
  "Escalated after hangup",
  "Escalated, transfer not available",
  "Non urgent took note",
]);

// Mister Sparky's hangup-reason pills, rebuilt on the engine's `reason`. v1's
// fourteen values fire on well under 1% of this tenant's interactions, so
// anything not listed simply falls through to the generic event-type path
// rather than earning a bespoke mapping.
const SPARKY_REASON_BADGES: Record<string, CustomerBadgeId> = {
  "Price shopping": "QuoteOnly",
  "Price objection": "ObjectPrice",
  "Timing objection": "TooLate",
  Disconnected: "Disconnect",
  "Went with Other": "WentWithOther",
};

// ARS escalation policy: an interaction is "Escalated" when it was a real
// LEAD that ended without a booking ("engaged, then dropped"). These are the
// engine outcomes (analytics.interaction_outcome.outcome) that, paired with
// interaction_group=LEAD, surface the "Escalated" pill. Booked / Transferred /
// Handled / Non-urgent-took-note are explicitly NOT escalated.
export const ARS_ESCALATED_OUTCOMES = [
  "Unbooked",
  "Escalated after hangup",
  "Escalated, transfer not available",
] as const;

const arsEscalatedOutcomes = new Set<string>(ARS_ESCALATED_OUTCOMES);

// Map an ARS interaction-outcome row (interaction_group + outcome from the
// config-driven engine) onto a badge id. Fully replaces the flagged_event_type
// path for ARS: LEAD + an unbooked-flavored outcome → "Escalated"; everything
// else renders its disposition as a neutral pill.
function arsInteractionOutcomeBadge(
  interactionGroup: string | null,
  outcome: string
): CustomerBadgeId | undefined {
  if (interactionGroup === "LEAD" && arsEscalatedOutcomes.has(outcome)) {
    return "escalation_rule";
  }
  if (interactionGroup === "NOT_ENOUGH_INFO") {
    return "NoResponse";
  }
  // Non-escalated dispositions, by engine outcome.
  switch (outcome) {
    case "Booked":
      return "appointment_booked";
    case "Transferred":
      return "Transferred";
    case "Non urgent took note":
      return "ars_took_note";
    case "Handled":
      return "ars_handled";
  }
  // Fall back to the interaction group for any outcome not enumerated above
  // (e.g. a non-lead "Unbooked").
  return "NotLead";
}

export const getInteractionBadge = (interaction: {
  event_type: string | null;
  flagged_event_type: string | null;
  is_flagged: boolean;

  /* ——— sparky specific badges ——— */
  is_transferred?: boolean;
  analytics_interaction_group?: string | null;
  analytics_outcome?: string | null;
  analytics_reason?: string | null;
  analytics_lead_status?: string | null;
  text_responded?: boolean | null;
  tenant_name?: string;
  is_booked_by_other?: boolean | null;

  /* ——— ARS interaction-outcome badges (gated by ARS_ESCALATION_OUTCOME_PILL) ——— */
  use_interaction_outcome?: boolean;
  interaction_group?: string | null;
  interaction_outcome?: string | null;
}): CustomerBadgeId | undefined => {
  // Check for is_booked_by_other to give it priority over other badges like transferred
  if (interaction.is_booked_by_other) {
    return "booked_by_other";
  }

  // ARS escalation policy fully replaces flagged_event_type: when enabled, use
  // the engine outcome when present and otherwise suppress legacy event pills.
  if (interaction.use_interaction_outcome) {
    if (!interaction.interaction_outcome) {
      return undefined;
    }

    return arsInteractionOutcomeBadge(
      interaction.interaction_group ?? null,
      interaction.interaction_outcome
    );
  }

  if (interaction.tenant_name === "Mister Sparky" && !interaction.is_flagged) {
    if (interaction.text_responded === false) {
      return "NoResponse";
    }

    if (
      interaction.analytics_interaction_group === "NON_LEAD" ||
      interaction.analytics_interaction_group === "NOT_ENOUGH_INFO"
    ) {
      return "NotLead";
    }

    if (
      interaction.is_transferred ||
      (!!interaction.analytics_outcome &&
        SPARKY_TRANSFERRED_OUTCOMES.has(interaction.analytics_outcome))
    ) {
      return "Transferred";
    }

    const reasonBadge =
      interaction.analytics_reason && SPARKY_REASON_BADGES[interaction.analytics_reason];
    if (reasonBadge) {
      return reasonBadge;
    }
  }
  const eventType = interaction.is_flagged
    ? interaction.flagged_event_type
    : interaction.event_type;
  // APPOINTMENT_FOUND (→ appointment_confirmed) is also set when the agent
  // checks existing appointments as plumbing inside a booking flow. A genuine
  // appointment inquiry is classified as a non-lead interaction
  // (analytics.interaction_outcome, projected to binary LEAD / NON_LEAD), so
  // only show the "Appt Confirmed" badge for those.
  if (eventType === "appointment_confirmed" && interaction.analytics_lead_status !== "NON_LEAD") {
    return undefined;
  }
  const badgeIds = Object.keys(BADGE_DEFS);
  return eventType && badgeIds.includes(eventType) ? (eventType as CustomerBadgeId) : undefined;
};

export const hasCustomerBadge = (interactionEvent: string) => {
  return Object.keys(BADGE_DEFS).includes(interactionEvent);
};

/* ────────────────────────────────────────────────────────────────── */
/* Derived, fully-typed helpers                                       */
/* ────────────────────────────────────────────────────────────────── */

// literal union of every badge key
export type CustomerBadgeId = keyof typeof BADGE_DEFS;

// Zod schema that auto-updates when you add a key above
export const BadgeSchema = z.enum(
  Object.keys(BADGE_DEFS) as [CustomerBadgeId, ...CustomerBadgeId[]]
);

/* ────────────────────────────────────────────────────────────────── */
/* Display                                                            */
/* ────────────────────────────────────────────────────────────────── */

export const FLAGGED_BADGE_IDS = [
  "billing_inquiry",
  "membership_inquiry",
  "membership_sold_but_not_paid",
  "scheduling_objection",
  "price_objection",
  "ask_for_human",
  "escalation_rule",
  "text_disabled",
  "manual_escalation",
  "campaign_dismissal",
  "service_area_issue",
  "project_follow_up",
  "no_appointments_found",
  "non_owner_occupant",
  "commercial",
  "attic_job",
  "boiler_job",
  "canceled",
  "account_update",
  "high_value",
  "no_interaction",
  "paid_lead",
  "paid_lead_inactivity",
  "paid_lead_no_response",
  "auto_dial",
  "landline",
  "carrier_filtered",
  "duplicate_booking",
  "outstanding_balance",
  // "other",
  "Transferred",
  // Appointment Reminders
  "dangling_appointment_reschedule",
  "dangling_appointment_cancel",

  // Hot lead flag
  "hot_lead",

  "move_up_requested",

  // Certus/PestPac escalation types
  "customer_care",
  "retention",
  "inside_sales",
  "outside_sales",

  // Bay Club escalation types
  "golf_inquiry",
  "membership_reactivation",
  "general_inquiry",
  "primary_cancellation",
  "add_on_removal",
  "corporate_cancellation",
  "account_request",

  // Tragar escalation types
  "oil_delivery",

  // Transfer-related flags
  "transfer_not_picked_up",
  "hung_up_before_transfer",

  // GBP 5-star review testimonial request
  "customer_testimonial",
] as const satisfies readonly CustomerBadgeId[];

export const SUCCESS_BADGE_IDS = [
  "appointment_booked",
  "emergency_services_notified",
  "booked_by_other",
] as const satisfies readonly CustomerBadgeId[];

export const MUTED_BADGE_IDS = [
  "ObjectRobot",
  "QuoteOnly",
  "ObjectPrice",
  "TooLate",
  "EmergencyServiceUnavailable",
  "Callback",
  "ToFollowUp",
  "WentWithOther",
  "SpeakToThirdParty",
  "Disconnect",
  "Finished",
  "FailConfirm",
  "TooManyQuestions",
  "Frustration",
  // ARS neutral dispositions
  "ars_handled",
  "ars_took_note",
] as const satisfies readonly CustomerBadgeId[];

export type BadgeColor = "success" | "accent" | "warning" | "muted";

const FLAGGED = new Set<CustomerBadgeId>(FLAGGED_BADGE_IDS as readonly CustomerBadgeId[]);
const SUCCESS = new Set<CustomerBadgeId>(SUCCESS_BADGE_IDS as readonly CustomerBadgeId[]);
const MUTED = new Set<CustomerBadgeId>(MUTED_BADGE_IDS as readonly CustomerBadgeId[]);

export const customerBadgeColor = (id: CustomerBadgeId): BadgeColor => {
  if (FLAGGED.has(id)) {
    return "warning";
  }
  if (SUCCESS.has(id)) {
    return "success";
  }
  if (MUTED.has(id)) {
    return "muted";
  }
  return "accent";
};

export const customerBadgeLabel = (id: CustomerBadgeId) => BADGE_DEFS[id];
